import React, { useEffect, useRef } from "react";
import { Element } from "react-scroll";
import { useSelector } from "react-redux";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import MainLogo from "../../../components/design/MainLogo";
import { scrollToFarmSection } from "./farmNavigationScroll";

export default function FarmStart() {
  const language = useSelector((state) => state.app.language);
  const sv = language === 0;
  const subtitle = ["Din lokala producent av färska och näringsrika ", "Your trusted local source for fresh and nutrient-packed "];
  const logoFrame = useRef(null);

  useEffect(() => {
    const element = logoFrame.current;
    if (!element) return undefined;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let frame;
    let lastTime;
    let current = 0;
    let target = 0;
    let distance = 32;

    const paint = () => {
      element.style.setProperty("--farm-logo-scroll", `${(current * distance).toFixed(2)}px`);
      element.style.setProperty("--farm-logo-rotate", `${(current * 2.5).toFixed(3)}deg`);
      element.style.setProperty("--farm-logo-scale", (1 - current * .03).toFixed(4));
    };
    const stop = () => {
      if (frame !== undefined) window.cancelAnimationFrame(frame);
      frame = undefined;
      lastTime = undefined;
    };
    const animate = (time) => {
      const elapsed = lastTime === undefined ? 16 : Math.min(time - lastTime, 64);
      lastTime = time;
      current += (target - current) * (1 - Math.exp(-elapsed / 110));
      if (Math.abs(target - current) < .0005) current = target;
      paint();
      if (current === target) stop();
      else frame = window.requestAnimationFrame(animate);
    };
    const measure = () => {
      if (reducedMotion.matches) {
        stop();
        current = target = 0;
        paint();
        return;
      }

      // Follow this card through the viewport, including its lower mobile position.
      const bounds = element.getBoundingClientRect();
      const viewport = window.innerHeight;
      const centre = bounds.top + bounds.height / 2;
      target = Math.max(-1, Math.min(1, (viewport / 2 - centre) / ((viewport + bounds.height) / 2)));
      distance = Math.min(32, bounds.height * .09);
      if (bounds.bottom <= 0 || bounds.top >= viewport) {
        stop();
        current = target;
        paint();
      } else if (frame === undefined) {
        frame = window.requestAnimationFrame(animate);
      }
    };

    window.addEventListener("scroll", measure, { passive: true });
    window.addEventListener("resize", measure);
    reducedMotion.addEventListener("change", measure);
    measure();
    return () => {
      window.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
      reducedMotion.removeEventListener("change", measure);
      stop();
    };
  }, []);

  return (
    <Element name="start" className="FarmStart" id="farm-start">
      <div className="FarmStartHero FarmLandingContainer">
        <div className="FarmStartCopy">
          <p className="FarmStartEyebrow">{sv ? "LOKALT ODLAT MICROGREENS" : "LOCALLY GROWN MICROGREENS"}</p>
          <h1>VERT<span>.farm</span></h1>
          <p className="FarmStartLead">{subtitle[language]}<em>Microgreens!</em></p>
          <div className="FarmStartActions">
            <button className="FarmLandingButton" type="button" onClick={() => scrollToFarmSection("farm/products")}>{sv ? "SORTIMENT" : "PRODUCTS"}<ArrowForwardIcon /></button>
            <button className="FarmLandingTextLink" type="button" onClick={() => scrollToFarmSection("farm/info")}>INFO<span>↘</span></button>
          </div>
        </div>
        <div className="FarmStartVisual" aria-hidden="true">
          <div className="FarmStartLogoFrame" ref={logoFrame}><MainLogo logoColor={false} textColor={true} noStartAni ambient /></div>
          <p className="FarmStartVisualCaption">{sv ? "ODLAT MED OMSORG" : "GROWN WITH CARE"}</p>
        </div>
      </div>
    </Element>
  );
}
